'use client'

import { useEffect, useReducer, useRef, type ReactElement } from 'react'
import {
  ACTIVITY_PRESETS,
  INPUT_BOUNDS,
  NUTRITION_GOALS,
  type InputField,
} from '../constants'
import {
  calculate,
  validateInputs,
  type CalculationResult,
  type CalculatorField,
  type RawCalculatorInput,
  type ValidationErrors,
} from '../lib/formula'
import { loadCalculation, saveCalculation, type SavedCalculation } from '../lib/storage'
import { CalculatorResults } from './CalculatorResults'

/**
 * The route's single `'use client'` island (SAD §4 pillar 2). One useReducer owns
 * the coupled transitions — happy path (AC-01), bounds/missing errors (AC-02/AC-07),
 * restore (AC-06/AC-09), and the calculating loading state — rather than scattered
 * useState kept manually in sync (SAD §4 pillar 3). Side effects (persist, GA4,
 * loading timer) stay in the event handlers; the reducer is pure.
 */

/** Perceived-feedback delay for the loading state before results reveal (ms). */
const CALCULATING_DELAY_MS = 450

const content = {
  legend: 'Твої дані',
  fields: {
    age: { label: 'Вік', placeholder: 'напр. 32' },
    height: { label: 'Зріст, см', placeholder: 'напр. 168' },
    weight: { label: 'Вага, кг', placeholder: 'напр. 62' },
    activity: { label: 'Рівень активності', placeholder: 'Обери варіант' },
    goal: { label: 'Твоя ціль', placeholder: 'Обери варіант' },
  },
  submit: 'Порахувати',
  reset: 'Порахувати заново',
  calculating: 'Рахую…',
  errors: {
    required: 'Заповни це поле.',
    select: 'Обери варіант.',
  },
  restore: {
    message: 'У тебе вже є збережений розрахунок на цьому пристрої. Відновити його?',
    accept: 'Відновити',
    dismiss: 'Почати заново',
  },
} as const

const EMPTY_INPUTS: RawCalculatorInput = {
  age: '',
  height: '',
  weight: '',
  activity: '',
  goal: '',
}

type Status = 'idle' | 'calculating'

interface FormState {
  inputs: RawCalculatorInput
  errors: ValidationErrors
  status: Status
  result: CalculationResult | null
  restoreOffer: SavedCalculation | null
}

const INITIAL_STATE: FormState = {
  inputs: EMPTY_INPUTS,
  errors: {},
  status: 'idle',
  result: null,
  restoreOffer: null,
}

type Action =
  | { type: 'CHANGE_FIELD'; field: CalculatorField; value: string }
  | { type: 'SET_ERRORS'; errors: ValidationErrors }
  | { type: 'START_CALCULATING' }
  | { type: 'SET_RESULT'; result: CalculationResult }
  | { type: 'OFFER_RESTORE'; saved: SavedCalculation }
  | { type: 'ACCEPT_RESTORE' }
  | { type: 'DISMISS_RESTORE' }
  | { type: 'RESET' }

function reducer(state: FormState, action: Action): FormState {
  switch (action.type) {
    case 'CHANGE_FIELD': {
      const restErrors = { ...state.errors }
      delete restErrors[action.field]
      return {
        ...state,
        inputs: { ...state.inputs, [action.field]: action.value },
        errors: restErrors,
      }
    }
    case 'SET_ERRORS':
      return { ...state, errors: action.errors, status: 'idle', result: null }
    case 'START_CALCULATING':
      return { ...state, status: 'calculating', errors: {}, result: null }
    case 'SET_RESULT':
      return { ...state, result: action.result, status: 'idle', errors: {} }
    case 'OFFER_RESTORE':
      return { ...state, restoreOffer: action.saved }
    case 'ACCEPT_RESTORE':
      if (!state.restoreOffer) return state
      return {
        ...state,
        inputs: state.restoreOffer.inputs,
        result: state.restoreOffer.result,
        status: 'idle',
        errors: {},
        restoreOffer: null,
      }
    case 'DISMISS_RESTORE':
      return { ...state, restoreOffer: null }
    case 'RESET':
      return INITIAL_STATE
    default:
      return state
  }
}

function fireEvent(name: string): void {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', name)
  }
}

function rangeMessage(field: InputField): string {
  const b = INPUT_BOUNDS[field]
  return `Вкажи значення від ${b.min} до ${b.max} ${b.unit}.`
}

function errorMessage(field: CalculatorField, kind: NonNullable<ValidationErrors[CalculatorField]>): string {
  if (field === 'activity' || field === 'goal') return content.errors.select
  if (kind === 'range') return rangeMessage(field)
  return content.errors.required
}

export function CalculatorForm(): ReactElement {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const saved = loadCalculation()
    if (saved) dispatch({ type: 'OFFER_RESTORE', saved })
  }, [])

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current)
  }, [])

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (state.status === 'calculating') return

    const validation = validateInputs(state.inputs)
    if (!validation.ok) {
      dispatch({ type: 'SET_ERRORS', errors: validation.errors })
      return
    }

    const inputs = state.inputs
    dispatch({ type: 'START_CALCULATING' })
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      const outcome = calculate(inputs)
      if (outcome.ok) {
        saveCalculation(inputs, outcome.result)
        fireEvent('calculator_complete')
        dispatch({ type: 'SET_RESULT', result: outcome.result })
      }
    }, CALCULATING_DELAY_MS)
  }

  const t = content
  const isCalculating = state.status === 'calculating'
  const inputBase =
    'w-full font-montserrat-alt text-base text-deep bg-white rounded-xl border px-4 py-3 outline-none transition-all duration-200 focus:ring-2 focus:ring-purple/30 focus:border-purple/40'
  const fieldError = (field: CalculatorField) => state.errors[field]
  const numberFields: readonly InputField[] = ['age', 'height', 'weight']

  return (
    <div className="max-w-2xl mx-auto">
      {state.restoreOffer && (
        <div
          role="dialog"
          aria-label="Відновити збережений розрахунок"
          className="mb-6 animate-fade-in-up bg-white rounded-2xl border border-purple/20 shadow-lg shadow-purple/5 p-5 md:p-6 flex flex-col sm:flex-row sm:items-center gap-4"
        >
          <p className="flex-1 font-montserrat-alt text-sm text-deep/85 leading-relaxed">
            {t.restore.message}
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => dispatch({ type: 'DISMISS_RESTORE' })}
              className="font-montserrat-alt text-sm font-semibold px-4 py-2 rounded-full border border-purple/20 text-purple hover:bg-purple/10 transition-all duration-200"
            >
              {t.restore.dismiss}
            </button>
            <button
              type="button"
              onClick={() => dispatch({ type: 'ACCEPT_RESTORE' })}
              className="font-montserrat-alt text-sm font-semibold px-4 py-2 rounded-full bg-rose text-deep hover:bg-cream transition-all duration-200"
            >
              {t.restore.accept}
            </button>
          </div>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        noValidate
        className="bg-white rounded-2xl md:rounded-3xl shadow-xl shadow-purple/5 border border-rose/10 p-6 md:p-8"
      >
        <fieldset disabled={isCalculating} className="transition-opacity duration-200 disabled:opacity-70">
          <legend className="font-alumni text-2xl md:text-3xl font-bold text-deep mb-6">
            {t.legend}
          </legend>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {numberFields.map((field) => {
              const err = fieldError(field)
              const describedBy = err ? `${field}-error` : undefined
              return (
                <div key={field}>
                  <label htmlFor={field} className="block font-montserrat-alt text-sm font-semibold text-deep mb-1.5">
                    {t.fields[field].label}
                  </label>
                  <input
                    id={field}
                    name={field}
                    type="number"
                    inputMode="numeric"
                    placeholder={t.fields[field].placeholder}
                    value={state.inputs[field]}
                    onChange={(e) => dispatch({ type: 'CHANGE_FIELD', field, value: e.target.value })}
                    aria-invalid={err ? true : undefined}
                    aria-describedby={describedBy}
                    className={`${inputBase} ${err ? 'border-red-400 focus:ring-red-200' : 'border-rose/20'}`}
                  />
                  {err && (
                    <p id={describedBy} className="mt-1 font-montserrat-alt text-xs text-red-600">
                      {errorMessage(field, err)}
                    </p>
                  )}
                </div>
              )
            })}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div>
              <label htmlFor="activity" className="block font-montserrat-alt text-sm font-semibold text-deep mb-1.5">
                {t.fields.activity.label}
              </label>
              <select
                id="activity"
                name="activity"
                value={state.inputs.activity}
                onChange={(e) => dispatch({ type: 'CHANGE_FIELD', field: 'activity', value: e.target.value })}
                aria-invalid={fieldError('activity') ? true : undefined}
                aria-describedby={fieldError('activity') ? 'activity-error' : undefined}
                className={`${inputBase} appearance-none ${fieldError('activity') ? 'border-red-400 focus:ring-red-200' : 'border-rose/20'}`}
              >
                <option value="" disabled>
                  {t.fields.activity.placeholder}
                </option>
                {ACTIVITY_PRESETS.map((preset) => (
                  <option key={preset.id} value={preset.id}>
                    {preset.label} — {preset.hint}
                  </option>
                ))}
              </select>
              {fieldError('activity') && (
                <p id="activity-error" className="mt-1 font-montserrat-alt text-xs text-red-600">
                  {t.errors.select}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="goal" className="block font-montserrat-alt text-sm font-semibold text-deep mb-1.5">
                {t.fields.goal.label}
              </label>
              <select
                id="goal"
                name="goal"
                value={state.inputs.goal}
                onChange={(e) => dispatch({ type: 'CHANGE_FIELD', field: 'goal', value: e.target.value })}
                aria-invalid={fieldError('goal') ? true : undefined}
                aria-describedby={fieldError('goal') ? 'goal-error' : undefined}
                className={`${inputBase} appearance-none ${fieldError('goal') ? 'border-red-400 focus:ring-red-200' : 'border-rose/20'}`}
              >
                <option value="" disabled>
                  {t.fields.goal.placeholder}
                </option>
                {NUTRITION_GOALS.map((goal) => (
                  <option key={goal.id} value={goal.id}>
                    {goal.label} — {goal.hint}
                  </option>
                ))}
              </select>
              {fieldError('goal') && (
                <p id="goal-error" className="mt-1 font-montserrat-alt text-xs text-red-600">
                  {t.errors.select}
                </p>
              )}
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              type="submit"
              aria-busy={isCalculating}
              className="inline-flex items-center justify-center gap-2 font-montserrat-alt font-semibold min-h-[52px] px-8 py-4 text-base rounded-full bg-rose text-deep hover:bg-cream hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:hover:scale-100 disabled:cursor-progress"
            >
              {isCalculating ? (
                <>
                  {t.calculating}
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={4} />
                    <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                </>
              ) : (
                <>
                  {state.result ? t.reset : t.submit}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
                  </svg>
                </>
              )}
            </button>
          </div>
        </fieldset>
      </form>

      {isCalculating && <ResultsSkeleton />}
      {!isCalculating && state.result && (
        <CalculatorResults key={state.result.energy} result={state.result} />
      )}
    </div>
  )
}

function ResultsSkeleton(): ReactElement {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Рахую твій орієнтир"
      className="mt-8 md:mt-10 bg-white rounded-2xl md:rounded-3xl border border-rose/10 p-6 md:p-10 overflow-hidden"
    >
      <div className="h-8 w-40 rounded-lg bg-rose/10 skeleton-light" />
      <div className="mt-6 h-28 rounded-2xl bg-deep/5 skeleton-light" />
      <div className="mt-6 grid grid-cols-3 gap-3 md:gap-4">
        <div className="h-20 rounded-xl bg-rose/10 skeleton-light" />
        <div className="h-20 rounded-xl bg-rose/10 skeleton-light" />
        <div className="h-20 rounded-xl bg-rose/10 skeleton-light" />
      </div>
      <span className="sr-only">Рахую…</span>
    </div>
  )
}
